import { useCallback, useEffect, useMemo, useState } from 'react';

import AdminConfirmDialog from '../admin/AdminConfirmDialog';
import AcademiaDateField from '../common/AcademiaDateField';
import AcademiaSelect from '../common/AcademiaSelect';
import AcademiaTimeField from '../common/AcademiaTimeField';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import AdminPagination from '../admin/AdminPagination';
import AdminToast from '../admin/AdminToast';
import FormateurPageShell from './FormateurPageShell';
import {
  archiveFormateurAttendance,
  fetchFormateurAttendance,
  fetchFormateurNotesContext,
  fetchFormateurPresenceRegistry,
  fetchFormateurSessions,
  formateurPresenceExportPdfPath,
  saveFormateurAttendance,
  saveFormateurNote,
  uploadFormateurNoteFile,
} from '../../services/academiaFormateurService';
import { downloadAuthenticatedFile } from '../../services/api';
import {
  attendanceDayKey,
  getFormateurAttendanceTodayKey,
  isFormateurAttendanceEditableDate,
} from '../../utils/formateurAttendanceDate';

const PRESENCE_PAGE_SIZE = 8;

type SessionRow = {
  id: string;
  jourSemaine?: string;
  dateSeance?: string | null;
  filiere?: { libelle?: string };
  cohorte?: { code?: string; libelle?: string } | null;
  vacation?: { libelle?: string };
  salle?: { code?: string };
};

const PRESENCE_LABELS: Record<string, string> = {
  PRESENT: 'Présent',
  ABSENT: 'Absent',
  RETARD: 'Retard',
  EXCUSE: 'Excusé',
};

type PresenceRegistryRow = {
  dateSeance: string;
  presencesCount: number;
  archivee: boolean;
  dateArchivage: string | null;
};

function formatRegistryDate(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatShortDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formateurDisplayName(user: FormateurSessionsPanelProps['user']) {
  return [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ') || user.telephone || 'Formateur';
}

interface FormateurSessionsPanelProps {
  user: {
    telephone?: string;
    profil?: { prenom?: string; nom?: string };
  };
}

function SessionPickerCard({
  session,
  selected,
  onSelect,
}: {
  session: SessionRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const hasCohorte = Boolean(session.cohorte?.code || session.cohorte?.libelle);
  const dateLabel = formatShortDate(session.dateSeance);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`formateur-session-card${selected ? ' formateur-session-card--active' : ''}`}
      aria-pressed={selected}
    >
      <span className="formateur-session-card__accent" aria-hidden />
      <span className="formateur-session-card__inner">
        <span className="formateur-session-card__top">
          <span className="formateur-session-card__icon">
            <LucideSymbolIcon name="event" tone="inherit" size={20} />
          </span>
          <span
            className={`formateur-session-card__status${hasCohorte ? '' : ' formateur-session-card__status--warn'}`}
          >
            {hasCohorte ? session.cohorte?.code ?? 'Cohorte' : 'Sans cohorte'}
          </span>
        </span>
        <span className="formateur-session-card__title">{session.filiere?.libelle ?? 'Formation'}</span>
        <dl className="formateur-session-card__meta">
          {session.jourSemaine ? (
            <div className="formateur-session-card__meta-row">
              <dt>Jour</dt>
              <dd>{session.jourSemaine}</dd>
            </div>
          ) : null}
          {dateLabel ? (
            <div className="formateur-session-card__meta-row">
              <dt>Date</dt>
              <dd>{dateLabel}</dd>
            </div>
          ) : null}
          {session.vacation?.libelle ? (
            <div className="formateur-session-card__meta-row">
              <dt>Vacation</dt>
              <dd>{session.vacation.libelle}</dd>
            </div>
          ) : null}
          {session.salle?.code ? (
            <div className="formateur-session-card__meta-row">
              <dt>Salle</dt>
              <dd>{session.salle.code}</dd>
            </div>
          ) : null}
        </dl>
        {selected ? (
          <span className="formateur-session-card__active-label">
            <LucideSymbolIcon name="check_circle" tone="inherit" size={14} />
            Session ouverte
          </span>
        ) : null}
      </span>
    </button>
  );
}

export default function FormateurSessionsPanel({ user }: FormateurSessionsPanelProps) {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [tab, setTab] = useState<'presences' | 'notes'>('presences');
  const [dateSeance, setDateSeance] = useState(() => getFormateurAttendanceTodayKey());
  const [attendance, setAttendance] = useState<{
    apprenants: {
      idInscription: string;
      nomComplet: string;
      matricule: string;
      statut: string | null;
      presenceId?: string | null;
    }[];
    feuille?: { archivee: boolean; dateArchivage: string | null };
  } | null>(null);
  const [notesRows, setNotesRows] = useState<
    {
      idInscription: string;
      nomComplet: string;
      matricule: string;
      notes: { id: string; typeLabel: string; noteSur20: number }[];
    }[]
  >([]);
  const [noteForm, setNoteForm] = useState({
    idInscription: '',
    typeEvaluation: 'DEVOIR',
    libelle: '',
    noteSur20: '10',
    urlFichier: '',
    juryCommentaire: '',
    creneauSoutenance: '',
  });
  const [noteFile, setNoteFile] = useState<File | null>(null);
  const [apprenantQuery, setApprenantQuery] = useState('');
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [savingPresences, setSavingPresences] = useState(false);
  const [archivingPresences, setArchivingPresences] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [presenceListFilter, setPresenceListFilter] = useState<'en_cours' | 'archivees'>('en_cours');
  const [presenceRegistry, setPresenceRegistry] = useState<PresenceRegistryRow[]>([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [presencePage, setPresencePage] = useState(1);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const loadSessions = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const rows = await fetchFormateurSessions();
      const list = Array.isArray(rows) ? rows : [];
      setSessions(list);
      setSessionId((prev) => (prev && list.some((s) => s.id === prev) ? prev : list[0]?.id ?? ''));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les sessions');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const loadAttendance = async () => {
    if (!sessionId) return;
    setAttendance(await fetchFormateurAttendance(sessionId, dateSeance));
  };

  const loadPresenceRegistry = useCallback(async () => {
    if (!sessionId) {
      setPresenceRegistry([]);
      return;
    }
    try {
      const data = await fetchFormateurPresenceRegistry(sessionId);
      setPresenceRegistry(Array.isArray(data?.feuilles) ? data.feuilles : []);
    } catch {
      setPresenceRegistry([]);
    }
  }, [sessionId]);

  const loadNotes = async () => {
    if (!sessionId) return;
    setNotesRows(await fetchFormateurNotesContext(sessionId));
  };

  useEffect(() => {
    if (!sessionId) return;
    if (tab === 'presences') {
      void loadAttendance();
      void loadPresenceRegistry();
    } else void loadNotes();
  }, [tab, sessionId, dateSeance, loadPresenceRegistry]);

  useEffect(() => {
    setPresencePage(1);
  }, [sessionId, dateSeance, tab, presenceListFilter]);

  const selected = useMemo(() => sessions.find((s) => s.id === sessionId) ?? null, [sessions, sessionId]);

  const archivedRegistry = useMemo(
    () => presenceRegistry.filter((row) => row.archivee),
    [presenceRegistry],
  );

  const enCoursRegistry = useMemo(
    () => presenceRegistry.filter((row) => !row.archivee && row.presencesCount > 0),
    [presenceRegistry],
  );

  const currentRegistryRow = useMemo(
    () =>
      presenceRegistry.find((row) => attendanceDayKey(row.dateSeance) === attendanceDayKey(dateSeance)) ??
      null,
    [presenceRegistry, dateSeance],
  );

  const presenceSheetArchived = attendance?.feuille?.archivee ?? false;
  const presenceSheetEditable =
    !presenceSheetArchived && isFormateurAttendanceEditableDate(dateSeance);
  const attendanceTodayKey = getFormateurAttendanceTodayKey();
  const canArchivePresenceSheet =
    presenceSheetEditable &&
    (attendance?.apprenants.some((a) => Boolean(a.presenceId)) ?? false);
  const canDownloadPresenceSheet = Boolean(
    currentRegistryRow?.archivee && (currentRegistryRow.presencesCount ?? 0) > 0,
  );

  const presenceTotal = attendance?.apprenants.length ?? 0;
  const presenceTotalPages = Math.max(1, Math.ceil(presenceTotal / PRESENCE_PAGE_SIZE));

  const paginatedApprenants = useMemo(() => {
    if (!attendance) return [];
    const start = (presencePage - 1) * PRESENCE_PAGE_SIZE;
    return attendance.apprenants.slice(start, start + PRESENCE_PAGE_SIZE);
  }, [attendance, presencePage]);

  const presencePaginationSummary = useMemo(() => {
    if (presenceTotal === 0) return 'Aucun apprenant';
    const start = (presencePage - 1) * PRESENCE_PAGE_SIZE + 1;
    const end = Math.min(presencePage * PRESENCE_PAGE_SIZE, presenceTotal);
    return `Affichage ${start}–${end} sur ${presenceTotal}`;
  }, [presencePage, presenceTotal]);

  useEffect(() => {
    if (presencePage > presenceTotalPages) {
      setPresencePage(presenceTotalPages);
    }
  }, [presencePage, presenceTotalPages]);

  const selectedApprenant = useMemo(
    () => notesRows.find((r) => r.idInscription === noteForm.idInscription) ?? null,
    [notesRows, noteForm.idInscription],
  );

  const filteredApprenants = useMemo(() => {
    const q = apprenantQuery.trim().toLowerCase();
    if (!q) return notesRows.slice(0, 8);
    return notesRows
      .filter((r) => {
        const nom = (r.nomComplet || '').toLowerCase();
        const matricule = (r.matricule || '').toLowerCase();
        return nom.includes(q) || matricule.includes(q);
      })
      .slice(0, 8);
  }, [notesRows, apprenantQuery]);

  const handleSavePresences = async () => {
    if (!sessionId || !attendance || !presenceSheetEditable) return;
    setSavingPresences(true);
    setError('');
    try {
      await saveFormateurAttendance(sessionId, {
        dateSeance,
        entries: attendance.apprenants.map((a) => ({
          idInscription: a.idInscription,
          statut: a.statut || 'PRESENT',
        })),
      });
      showToast('Présences enregistrées avec succès.', 'success');
      await loadAttendance();
      await loadPresenceRegistry();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Échec de l’enregistrement des présences.', 'error');
    } finally {
      setSavingPresences(false);
    }
  };

  const handleArchivePresences = () => {
    if (!sessionId || !attendance || !presenceSheetEditable) return;
    setArchiveConfirmOpen(true);
  };

  const confirmArchivePresences = async () => {
    if (!sessionId || !presenceSheetEditable) return;
    setArchivingPresences(true);
    setError('');
    try {
      await archiveFormateurAttendance(sessionId, { dateSeance });
      setArchiveConfirmOpen(false);
      showToast('Feuille de présences archivée.', 'success');
      await loadAttendance();
      await loadPresenceRegistry();
      setPresenceListFilter('archivees');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Échec de l’archivage.', 'error');
    } finally {
      setArchivingPresences(false);
    }
  };

  const handleDownloadPresencePdf = async () => {
    if (!sessionId || !canDownloadPresenceSheet) return;
    setDownloadingPdf(true);
    try {
      await downloadAuthenticatedFile(
        formateurPresenceExportPdfPath(sessionId, dateSeance),
        `presences-${dateSeance.slice(0, 10)}.pdf`,
      );
      showToast('Feuille téléchargée.', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Téléchargement impossible.', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const openRegistryDate = (iso: string) => {
    setDateSeance(attendanceDayKey(iso));
    void loadAttendance();
  };

  const handleSaveNote = async () => {
    if (!sessionId || !noteForm.idInscription) return;
    setSavingNote(true);
    setError('');
    try {
      let urlFichier = noteForm.urlFichier || undefined;
      if (noteFile) {
        urlFichier = await uploadFormateurNoteFile(noteFile);
      }
      await saveFormateurNote(sessionId, {
        idInscription: noteForm.idInscription,
        typeEvaluation: noteForm.typeEvaluation,
        libelle: noteForm.libelle,
        noteSur20: Number(noteForm.noteSur20),
        urlFichier,
        juryCommentaire: noteForm.juryCommentaire || undefined,
        creneauSoutenance: noteForm.creneauSoutenance || undefined,
      });
      showToast('Note enregistrée avec succès.', 'success');
      setNoteFile(null);
      await loadNotes();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Échec de l’enregistrement de la note.', 'error');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <>
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
      <FormateurPageShell
      title="Mes sessions assignées"
      description="Choisissez une session, puis enregistrez les présences ou les notes de vos apprenants."
      isLoading={isLoading}
      isError={Boolean(error) && sessions.length === 0}
      errorMessage={error}
      onRetry={() => void loadSessions()}
      sideCard={{
        label: 'Sessions actives',
        value: isLoading ? '…' : String(sessions.length),
        helper: selected
          ? `${selected.filiere?.libelle ?? 'Formation'} · ${selected.cohorte?.code ?? 'sans cohorte'}`
          : formateurDisplayName(user),
        sideLinks: [
          { label: 'Accueil', to: '/formateur', icon: 'dashboard' },
          { label: 'Rémunération', to: '/formateur/remuneration', icon: 'payments' },
        ],
      }}
    >

      <section className="formateur-sessions-list" aria-labelledby="formateur-sessions-picker-heading">
        <header className="formateur-sessions-list__header">
          <span className="formateur-sessions-list__header-icon" aria-hidden>
            <LucideSymbolIcon name="event" tone="inherit" size={22} />
          </span>
          <div>
            <h2 id="formateur-sessions-picker-heading" className="formateur-sessions-list__title">
              Vos sessions
            </h2>
            <p className="formateur-sessions-list__subtitle">
              Cliquez sur une carte pour ouvrir la saisie des présences ou des notes.
            </p>
          </div>
        </header>
        <div className="formateur-sessions-list__body">
          {sessions.length === 0 && !isLoading ? (
            <p className="formateur-sessions-empty">Aucune session ne vous est assignée pour le moment.</p>
          ) : (
            <div className="formateur-sessions-list__grid">
              {sessions.map((s) => (
                <SessionPickerCard
                  key={s.id}
                  session={s}
                  selected={s.id === sessionId}
                  onSelect={() => {
                    setSessionId(s.id);
                    setDateSeance(getFormateurAttendanceTodayKey());
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {selected ? (
        <section className="formateur-sessions-workspace formateur-portal" aria-label="Saisie session">
          <header className="formateur-sessions-workspace__head">
            <div className="min-w-0">
              <p className="formateur-sessions-workspace__eyebrow">Session sélectionnée</p>
              <h3 className="formateur-sessions-workspace__title">{selected.filiere?.libelle ?? 'Formation'}</h3>
              <p className="formateur-sessions-workspace__chips">
                {selected.cohorte?.code ? (
                  <span className="formateur-sessions-workspace__chip">{selected.cohorte.code}</span>
                ) : (
                  <span className="formateur-sessions-workspace__chip formateur-sessions-workspace__chip--warn">
                    Sans cohorte
                  </span>
                )}
                {selected.vacation?.libelle ? (
                  <span className="formateur-sessions-workspace__chip">{selected.vacation.libelle}</span>
                ) : null}
                {selected.salle?.code ? (
                  <span className="formateur-sessions-workspace__chip">{selected.salle.code}</span>
                ) : null}
              </p>
            </div>
            <div className="formateur-sessions-tabs" role="tablist" aria-label="Type de saisie">
              {(['presences', 'notes'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  className={`formateur-sessions-tabs__btn${tab === t ? ' formateur-sessions-tabs__btn--active' : ''}`}
                  onClick={() => setTab(t)}
                >
                  <LucideSymbolIcon
                    name={t === 'presences' ? 'check_circle' : 'assignment'}
                    tone="inherit"
                    size={16}
                  />
                  {t === 'presences' ? 'Présences' : 'Notes'}
                </button>
              ))}
            </div>
          </header>

          {!selected.cohorte ? (
            <p className="formateur-sessions-alert">
              <LucideSymbolIcon name="warning" tone="inherit" size={18} />
              Cette session n’a pas encore de cohorte — demandez à l’administration de la lier au planning.
            </p>
          ) : null}

          {tab === 'presences' ? (
            <div className="formateur-sessions-panel" role="tabpanel">
              <p className="formateur-sessions-alert formateur-sessions-alert--info">
                <LucideSymbolIcon name="info" tone="inherit" size={18} />
                La confirmation des présences n&apos;est possible que pour la <strong>date du jour</strong>.
                Consultez les feuilles passées via <strong>Archivées</strong> (lecture seule).
              </p>
              <div className="formateur-sessions-panel__toolbar formateur-sessions-panel__toolbar--presence">
                <div className="formateur-presence-toolbar-inline">
                  <AcademiaDateField
                    value={dateSeance}
                    onChange={setDateSeance}
                    label="Date de la séance"
                    className="formateur-presence-date"
                    disabled
                    minDate={attendanceTodayKey}
                    maxDate={attendanceTodayKey}
                  />
                  <div className="formateur-presence-actions" role="group" aria-label="Actions feuille de présences">
                  <div className="formateur-presence-filter" role="tablist" aria-label="Filtre des feuilles">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={presenceListFilter === 'en_cours'}
                      className={`formateur-presence-filter__btn${presenceListFilter === 'en_cours' ? ' formateur-presence-filter__btn--active' : ''}`}
                      onClick={() => setPresenceListFilter('en_cours')}>
                      En cours ({enCoursRegistry.length})
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={presenceListFilter === 'archivees'}
                      className={`formateur-presence-filter__btn${presenceListFilter === 'archivees' ? ' formateur-presence-filter__btn--active' : ''}`}
                      onClick={() => setPresenceListFilter('archivees')}>
                      Archivées ({archivedRegistry.length})
                    </button>
                  </div>
                  {canDownloadPresenceSheet ? (
                    <button
                      type="button"
                      className="formateur-sessions-btn formateur-sessions-btn--secondary"
                      disabled={downloadingPdf}
                      onClick={() => void handleDownloadPresencePdf()}>
                      <LucideSymbolIcon name="download" tone="inherit" size={16} />
                      {downloadingPdf ? 'Téléchargement…' : 'Télécharger PDF'}
                    </button>
                  ) : null}
                  </div>
                </div>
                {attendance ? (
                  <p className="formateur-sessions-panel__count">
                    {attendance.apprenants.length} apprenant
                    {attendance.apprenants.length !== 1 ? 's' : ''}
                    {presenceSheetArchived ? ' · archivée' : ''}
                  </p>
                ) : null}
              </div>

              {presenceListFilter === 'archivees' ? (
                <div className="formateur-presence-registry">
                  <p className="formateur-presence-registry__title">Feuilles archivées — session sélectionnée</p>
                  {archivedRegistry.length === 0 ? (
                    <p className="formateur-presence-registry__empty">Aucune feuille archivée pour cette session.</p>
                  ) : (
                    <ul className="formateur-presence-registry__list">
                      {archivedRegistry.map((row) => {
                        const day = attendanceDayKey(row.dateSeance);
                        const active = day === attendanceDayKey(dateSeance);
                        return (
                          <li key={row.dateSeance}>
                            <button
                              type="button"
                              className={`formateur-presence-registry__item${active ? ' formateur-presence-registry__item--active' : ''}`}
                              onClick={() => openRegistryDate(row.dateSeance)}>
                              <span className="formateur-presence-registry__date">{formatRegistryDate(row.dateSeance)}</span>
                              <span className="formateur-presence-registry__meta">
                                {row.presencesCount} présence{row.presencesCount !== 1 ? 's' : ''}
                                {row.dateArchivage
                                  ? ` · archivée le ${formatRegistryDate(row.dateArchivage)}`
                                  : ''}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : enCoursRegistry.length > 0 ? (
                <div className="formateur-presence-registry formateur-presence-registry--compact">
                  <p className="formateur-presence-registry__title">Saisies en cours (non archivées)</p>
                  <ul className="formateur-presence-registry__list">
                    {enCoursRegistry.map((row) => {
                      const active = attendanceDayKey(row.dateSeance) === attendanceDayKey(dateSeance);
                      return (
                        <li key={row.dateSeance}>
                          <button
                            type="button"
                            className={`formateur-presence-registry__item${active ? ' formateur-presence-registry__item--active' : ''}`}
                            onClick={() => openRegistryDate(row.dateSeance)}>
                            <span className="formateur-presence-registry__date">{formatRegistryDate(row.dateSeance)}</span>
                            <span className="formateur-presence-registry__meta">
                              {row.presencesCount} présence{row.presencesCount !== 1 ? 's' : ''}
                              {isFormateurAttendanceEditableDate(row.dateSeance) ? ' · modifiable' : ' · consultation'}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}

              {presenceSheetArchived ? (
                <p className="formateur-sessions-alert formateur-sessions-alert--info">
                  <LucideSymbolIcon name="inventory_2" tone="inherit" size={18} />
                  Feuille archivée
                  {attendance?.feuille?.dateArchivage
                    ? ` le ${new Date(attendance.feuille.dateArchivage).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}`
                    : ''}
                  . Consultation seule — les présences ne peuvent plus être modifiées.
                </p>
              ) : !presenceSheetEditable ? (
                <p className="formateur-sessions-alert formateur-sessions-alert--info">
                  <LucideSymbolIcon name="lock" tone="inherit" size={18} />
                  Consultation seule pour cette date : seule la feuille du jour peut être confirmée ou archivée.
                  <button
                    type="button"
                    className="ml-2 font-bold text-primary underline"
                    onClick={() => setDateSeance(getFormateurAttendanceTodayKey())}>
                    Ouvrir aujourd&apos;hui
                  </button>
                </p>
              ) : null}

              {!attendance ? (
                <p className="formateur-sessions-panel__loading">Chargement de la liste…</p>
              ) : attendance.apprenants.length === 0 ? (
                <p className="formateur-sessions-empty">Aucun apprenant inscrit sur cette cohorte.</p>
              ) : (
                <>
                  <ul className="formateur-presence-list">
                    {paginatedApprenants.map((a) => (
                      <li key={a.idInscription} className="formateur-presence-row">
                        <div className="formateur-presence-row__identity">
                          <span className="formateur-presence-row__avatar" aria-hidden>
                            {(a.nomComplet || '?').charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="formateur-presence-row__name">{a.nomComplet}</p>
                            <p className="formateur-presence-row__mat">{a.matricule}</p>
                          </div>
                        </div>
                        <AcademiaSelect
                          fieldSize="sm"
                          className="formateur-presence-row__select"
                          value={a.statut || 'PRESENT'}
                          disabled={!presenceSheetEditable}
                          onChange={(e) => {
                            const statut = e.target.value;
                            setAttendance((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    apprenants: prev.apprenants.map((x) =>
                                      x.idInscription === a.idInscription ? { ...x, statut } : x,
                                    ),
                                  }
                                : prev,
                            );
                          }}
                        >
                          {Object.entries(PRESENCE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </AcademiaSelect>
                      </li>
                    ))}
                  </ul>
                  {presenceTotal > PRESENCE_PAGE_SIZE ? (
                    <AdminPagination
                      className="formateur-presence-pagination"
                      currentPage={presencePage}
                      totalPages={presenceTotalPages}
                      onPageChange={setPresencePage}
                      summary={presencePaginationSummary}
                    />
                  ) : null}
                </>
              )}

              <footer className="formateur-sessions-panel__footer">
                {presenceSheetEditable ? (
                  <>
                    <button
                      type="button"
                      className="formateur-sessions-btn formateur-sessions-btn--primary"
                      disabled={!attendance?.apprenants.length || savingPresences}
                      onClick={() => void handleSavePresences()}
                    >
                      {savingPresences ? 'Enregistrement…' : 'Enregistrer les présences'}
                    </button>
                    <button
                      type="button"
                      className="formateur-sessions-btn formateur-sessions-btn--secondary"
                      disabled={!canArchivePresenceSheet || archivingPresences || savingPresences}
                      onClick={() => void handleArchivePresences()}
                      title={
                        canArchivePresenceSheet
                          ? 'Verrouiller la feuille après enregistrement'
                          : 'Enregistrez d’abord au moins une présence'
                      }
                    >
                      {archivingPresences ? 'Archivage…' : 'Archiver la feuille'}
                    </button>
                  </>
                ) : (
                  <p className="formateur-sessions-panel__readonly-hint">
                    {presenceSheetArchived
                      ? 'Feuille en lecture seule.'
                      : 'Saisie bloquée — uniquement la date du jour est modifiable.'}
                  </p>
                )}
              </footer>
            </div>
          ) : (
            <div className="formateur-sessions-panel" role="tabpanel">
              <div className="formateur-notes-form">
                <div className="formateur-notes-form__section">
                  <h4 className="formateur-notes-form__heading">
                    <LucideSymbolIcon name="person_search" tone="inherit" size={18} />
                    Apprenant
                  </h4>
                  <input
                    className="academia-field w-full"
                    placeholder="Rechercher par nom ou matricule…"
                    value={apprenantQuery}
                    onChange={(e) => {
                      const next = e.target.value;
                      setApprenantQuery(next);
                      if (!next.trim()) {
                        setNoteForm((f) => ({ ...f, idInscription: '' }));
                      }
                    }}
                  />
                  <ul className="formateur-notes-picker">
                    {filteredApprenants.length === 0 ? (
                      <li className="formateur-notes-picker__empty">Aucun apprenant trouvé.</li>
                    ) : (
                      filteredApprenants.map((r) => (
                        <li key={r.idInscription}>
                          <button
                            type="button"
                            className={`formateur-notes-picker__item${
                              noteForm.idInscription === r.idInscription
                                ? ' formateur-notes-picker__item--active'
                                : ''
                            }`}
                            onClick={() => {
                              setNoteForm((f) => ({ ...f, idInscription: r.idInscription }));
                              setApprenantQuery(`${r.matricule} — ${r.nomComplet}`);
                            }}
                          >
                            <span className="formateur-notes-picker__mat">{r.matricule}</span>
                            <span className="formateur-notes-picker__name">{r.nomComplet}</span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                  {selectedApprenant ? (
                    <p className="formateur-notes-form__hint">
                      Sélectionné : {selectedApprenant.matricule} — {selectedApprenant.nomComplet}
                    </p>
                  ) : null}
                </div>

                <div className="formateur-notes-form__section formateur-notes-form__grid">
                  <h4 className="formateur-notes-form__heading formateur-notes-form__heading--full">
                    <LucideSymbolIcon name="assignment" tone="inherit" size={18} />
                    Évaluation
                  </h4>
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Type</span>
                    <AcademiaSelect
                      className="w-full"
                      value={noteForm.typeEvaluation}
                      onChange={(e) => setNoteForm((f) => ({ ...f, typeEvaluation: e.target.value }))}
                    >
                      {['DEVOIR', 'TP', 'SOUTENANCE', 'EXAMEN', 'PROJET'].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </AcademiaSelect>
                  </label>
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Libellé</span>
                    <input
                      className="academia-field w-full"
                      placeholder="Ex. TP 1, Examen final"
                      value={noteForm.libelle}
                      onChange={(e) => setNoteForm((f) => ({ ...f, libelle: e.target.value }))}
                    />
                  </label>
                  <label className="formateur-notes-field">
                    <span className="formateur-notes-field__label">Note /20</span>
                    <input
                      type="number"
                      min={0}
                      max={20}
                      step={0.5}
                      className="academia-field w-full"
                      value={noteForm.noteSur20}
                      onChange={(e) => setNoteForm((f) => ({ ...f, noteSur20: e.target.value }))}
                    />
                  </label>
                  <label className="formateur-notes-field formateur-notes-field--file">
                    <span className="formateur-notes-field__label">Pièce jointe (optionnel)</span>
                    <input
                      type="file"
                      className="academia-field w-full file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-primary"
                      onChange={(e) => setNoteFile(e.target.files?.[0] ?? null)}
                    />
                    {noteFile ? (
                      <span className="formateur-notes-field__hint">{noteFile.name}</span>
                    ) : null}
                  </label>
                  <label className="formateur-notes-field formateur-notes-field--full">
                    <span className="formateur-notes-field__label">Commentaire jury</span>
                    <textarea
                      rows={3}
                      className="academia-field w-full"
                      placeholder="Observation / appréciation"
                      value={noteForm.juryCommentaire}
                      onChange={(e) => setNoteForm((f) => ({ ...f, juryCommentaire: e.target.value }))}
                    />
                  </label>
                  <div className="formateur-notes-field formateur-notes-field--full">
                    <span className="formateur-notes-field__label">Créneau soutenance (optionnel)</span>
                    <div className="formateur-notes-datetime">
                      <AcademiaDateField
                        hideLabel
                        className="max-w-none w-full"
                        value={
                          noteForm.creneauSoutenance.slice(0, 10) ||
                          new Date().toISOString().slice(0, 10)
                        }
                        onChange={(d) => {
                          const time =
                            noteForm.creneauSoutenance.length >= 16
                              ? noteForm.creneauSoutenance.slice(11, 16)
                              : '09:00';
                          setNoteForm((f) => ({ ...f, creneauSoutenance: `${d}T${time}` }));
                        }}
                      />
                      <AcademiaTimeField
                        hideLabel
                        className="w-full"
                        value={
                          noteForm.creneauSoutenance.length >= 16
                            ? noteForm.creneauSoutenance.slice(11, 16)
                            : '09:00'
                        }
                        onChange={(time) => {
                          const date =
                            noteForm.creneauSoutenance.slice(0, 10) ||
                            new Date().toISOString().slice(0, 10);
                          setNoteForm((f) => ({
                            ...f,
                            creneauSoutenance: `${date}T${time}`,
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <footer className="formateur-sessions-panel__footer">
                <button
                  type="button"
                  className="formateur-sessions-btn formateur-sessions-btn--primary"
                  disabled={!noteForm.idInscription || savingNote}
                  onClick={() => void handleSaveNote()}
                >
                  {savingNote ? 'Enregistrement…' : 'Enregistrer la note'}
                </button>
              </footer>
            </div>
          )}
        </section>
      ) : null}
    </FormateurPageShell>
      <AdminConfirmDialog
        open={archiveConfirmOpen}
        title="Archiver la feuille de présences"
        description={
          <p>
            Cette feuille ne pourra plus être modifiée. Vous pourrez uniquement la consulter en lecture
            seule.
          </p>
        }
        confirmLabel="Archiver"
        cancelLabel="Annuler"
        variant="neutral"
        isConfirming={archivingPresences}
        onConfirm={() => void confirmArchivePresences()}
        onCancel={() => {
          if (!archivingPresences) setArchiveConfirmOpen(false);
        }}
      />
    </>
  );
}
